/**
 * Canonical user-facing copy for the trip pivot / cancellation dialogs.
 * Mirrors the product "Content updates" matrix — keep this file the single
 * source of truth for modal titles, descriptions and action labels.
 */
import type { ChangeEventType } from "@/lib/change-events";

export interface ChangeEventCopyContext {
  orderUniqueId?: string | null;
  /** For `trip_type_updated`: the previous / next trip type labels. */
  fromLabel?: string | null;
  toLabel?: string | null;
}

export interface ChangeEventCopy {
  title: string;
  description: string;
  /** Label rendered above the reason dropdown. */
  reasonLabel: string;
  confirmLabel: string;
  cancelLabel: string;
}

const EXTERNAL_TRIP_NOTE = (what: string) =>
  `This order is already confirmed with an external trip. Changing the ${what} will cancel the existing external trip and create a new one.`;

export const NOTES_LABEL = "Notes";
export const NOTES_HELPER = "Required when the reason code is Other.";
/** Helper text when notes are always shown but only required for "Other". */
export const NOTES_HELPER_OPTIONAL =
  "Add any additional context (optional).";
export const SECONDARY_ACTION_LABEL = "Nevermind";

export function changeEventCopy(
  eventType: ChangeEventType,
  ctx: ChangeEventCopyContext = {},
): ChangeEventCopy {
  const id = ctx.orderUniqueId ?? "";
  switch (eventType) {
    case "trip_cancelled":
      return {
        title: id ? `Cancel Order ${id}?` : "Cancel Order?",
        description: "This action cannot be undone.",
        reasonLabel: "Reason for cancellation",
        confirmLabel: "Cancel Order",
        cancelLabel: SECONDARY_ACTION_LABEL,
      };
    case "trip_equipment_tractor":
      return {
        title: "Change Truck?",
        description: EXTERNAL_TRIP_NOTE("truck"),
        reasonLabel: "Reason for pivot",
        confirmLabel: "Change Truck",
        cancelLabel: SECONDARY_ACTION_LABEL,
      };
    case "trip_equipment_trailer":
      return {
        title: "Change Trailer?",
        description: EXTERNAL_TRIP_NOTE("trailer"),
        reasonLabel: "Reason for pivot",
        confirmLabel: "Change Trailer",
        cancelLabel: SECONDARY_ACTION_LABEL,
      };
    case "trip_type_updated": {
      const from = ctx.fromLabel?.trim();
      const to = ctx.toLabel?.trim();
      return {
        title:
          from && to ? `Change from ${from} to ${to}?` : "Change trip type?",
        description: EXTERNAL_TRIP_NOTE("trip type"),
        reasonLabel: "Reason for pivot",
        confirmLabel: "Change trip type",
        cancelLabel: SECONDARY_ACTION_LABEL,
      };
    }
    case "trip_created_manual":
      return {
        title: "Should this order be performed in Manual?",
        description:
          "You are required to provide a reason why this order needs to be performed in Manual.",
        reasonLabel: "Reason for manual trip type",
        confirmLabel: "Confirm",
        cancelLabel: SECONDARY_ACTION_LABEL,
      };
  }
}

/** Copy used when a single submit triggers multiple pivots at once. */
export function multiChangeCopy(ctx: ChangeEventCopyContext = {}): ChangeEventCopy {
  return {
    title: "Confirm changes?",
    description: ctx.orderUniqueId
      ? `This order is already confirmed with an external trip. These changes on ${ctx.orderUniqueId} will cancel the existing external trip and create a new one.`
      : "This order is already confirmed with an external trip. These changes will cancel the existing external trip and create a new one.",
    reasonLabel: "Reason for pivot",
    confirmLabel: "Confirm changes",
    cancelLabel: SECONDARY_ACTION_LABEL,
  };
}
